import type { ExportTableOptions } from '@mss_creator/shared';
import type { ReactNode } from 'react';
import { type ExportControlId, isControlVisible } from './export-format-controls';
import { ExportSegmentedControl } from './export-segmented-control';
import type { SegmentOption } from './export-segmented-control';
import { ExportSelectField } from './export-select-field';
import type { ExportFormat } from './export-selection';
import { ExportToggleField } from './export-toggle-field';

interface ExportTableControlsProps {
  /** Picks the controls shown, through the per-format visibility table. */
  format: ExportFormat;
  table: ExportTableOptions;
  onChange: (table: ExportTableOptions) => void;
  /** Decision L6: false when the export fits one tile, where the flag is inert. */
  repeatHeadersEffective: boolean;
  /** The selection's levier-names field, owned by the page (not option state). */
  levierNamesControl?: ReactNode;
}

const DENSITY_OPTIONS: readonly SegmentOption<ExportTableOptions['density']>[] = [
  { value: 'compact', label: 'Compacte' },
  { value: 'normal', label: 'Normale' },
  { value: 'aere', label: 'Aérée' },
];

const ROW_HEIGHT_OPTIONS: readonly SegmentOption<ExportTableOptions['rowHeightMode']>[] = [
  { value: 'contenu', label: 'Selon le contenu' },
  { value: 'ecran', label: "Comme à l'écran" },
];

const STRUCTURE_OPTIONS: readonly SegmentOption<ExportTableOptions['structure']>[] = [
  { value: 'colonnes', label: 'Colonnes' },
  { value: 'transpose', label: 'Transposé' },
];

/** The « Tableau » category: density, row heights, headers, levier names, structure. */
export function ExportTableControls({
  format,
  table,
  onChange,
  repeatHeadersEffective,
  levierNamesControl,
}: ExportTableControlsProps) {
  const shows = (id: ExportControlId) => isControlVisible(format, id);
  return (
    <>
      {shows('density') && (
        <ExportSegmentedControl
          label="Densité"
          value={table.density}
          options={DENSITY_OPTIONS}
          onChange={(density) => onChange({ ...table, density })}
        />
      )}
      {shows('rowHeightMode') && (
        <ExportSelectField
          label="Hauteur des lignes"
          value={table.rowHeightMode}
          options={ROW_HEIGHT_OPTIONS}
          onChange={(rowHeightMode) => onChange({ ...table, rowHeightMode })}
        />
      )}
      {shows('repeatHeaders') && (
        // Decision L6 — on a single-tile export the flag is provably inert, so
        // say so rather than offer a dead checkbox.
        <ExportToggleField
          label="Répéter les en-têtes sur chaque page"
          checked={table.repeatHeaders}
          disabled={!repeatHeadersEffective}
          hint={repeatHeadersEffective ? null : 'Export sur une seule page — sans effet.'}
          onChange={(repeatHeaders) => onChange({ ...table, repeatHeaders })}
        />
      )}
      {shows('levierNames') && levierNamesControl}
      {shows('structure') && (
        <ExportSegmentedControl
          label="Structure"
          value={table.structure}
          options={STRUCTURE_OPTIONS}
          onChange={(structure) => onChange({ ...table, structure })}
        />
      )}
    </>
  );
}
